// Карточка договора: реквизиты, workflow статусов, загрузка УКЭП, таймлайн.
import { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Upload, CheckCircle2, Clock } from 'lucide-react';
import { contractsApi, getErrorMessage } from '@/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney, formatDate, formatDateTime, contractStatusColor } from '@/lib/format';
import type { ContractStatus } from '@/types';
import './contract-detail.css';

const NEXT_STATUS: Record<ContractStatus, { value: ContractStatus; label: string }[]> = {
  draft: [{ value: 'negotiation', label: 'На согласование' }, { value: 'signing', label: 'На подпись' }],
  negotiation: [{ value: 'signing', label: 'На подпись' }],
  signing: [{ value: 'in_work', label: 'В работу' }],
  in_work: [{ value: 'closed', label: 'Закрыть' }],
  closed: [{ value: 'warranty', label: 'В гарантию' }],
  warranty: [],
  terminated: [],
};

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const contractId = Number(id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => contractsApi.get(contractId),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => contractsApi.changeStatus(contractId, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract', contractId] }); toast('Статус изменён', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const uploadMutation = useMutation({
    mutationFn: (file: File) => contractsApi.uploadSigned(contractId, file),
    onSuccess: (sig) => {
      qc.invalidateQueries({ queryKey: ['contract', contractId] });
      if (sig.signature_detected) toast(`Подпись обнаружена: ${sig.signers.map((s) => s.name).join(', ')}`, 'success');
      else toast('Файл загружен, подпись не обнаружена', 'info');
    },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const confirmMutation = useMutation({
    mutationFn: () => contractsApi.confirmSignature(contractId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract', contractId] }); toast('Подписание подтверждено, договор в работе', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  if (isLoading) return <AppLayout><Spinner label="Загрузка договора…" /></AppLayout>;
  if (!contract) return <AppLayout><div>Договор не найден</div></AppLayout>;

  const canEdit = can('contracts', 'change');

  return (
    <AppLayout>
      <button className="back-btn" onClick={() => navigate('/contracts')}><ArrowLeft size={16} /> К списку договоров</button>
      <div className="contract-head">
        <div>
          <div className="contract-head__number">{contract.number}</div>
          <div className="contract-head__sub">{contract.type_display} · {contract.counterparty_detail?.name}</div>
        </div>
        <StatusBadge label={contract.status_display} color={contractStatusColor(contract.status)} />
      </div>
      <div className="contract-grid">
        <div className="contract-main">
          <div className="card">
            <h3 className="card-title">Основное</h3>
            <dl className="detail-list">
              <Row label="Сумма договора" value={formatMoney(contract.total_amount)} mono />
              <Row label="Аванс" value={`${formatMoney(contract.advance_amount)} (${contract.advance_percent}%)`} mono />
              <Row label="Объект" value={contract.object_address || '—'} />
              <Row label="Начало работ" value={formatDate(contract.date_start)} mono />
              <Row label="Окончание" value={formatDate(contract.date_end)} mono />
              <Row label="Гарантия" value={`${contract.warranty_months} мес.`} mono />
              <Row label="Гарантийное удержание" value={`${contract.retention_percent}%`} mono />
              <Row label="Ответственный" value={contract.responsible_name || '—'} />
            </dl>
          </div>
          {contract.counterparty_detail && (
            <div className="card">
              <h3 className="card-title">Контрагент</h3>
              <dl className="detail-list">
                <Row label="Наименование" value={contract.counterparty_detail.name} />
                <Row label="ИНН" value={contract.counterparty_detail.inn} mono />
                <Row label="Руководитель" value={contract.counterparty_detail.director_name || '—'} />
              </dl>
            </div>
          )}
          <div className="card">
            <h3 className="card-title">История</h3>
            {contract.events.length === 0 ? <p className="muted-text">Событий пока нет</p> : (
              <div className="timeline">
                {contract.events.map((ev) => (
                  <div key={ev.id} className="timeline__item">
                    <div className="timeline__dot" />
                    <div className="timeline__body">
                      <div className="timeline__desc">{ev.description}</div>
                      <div className="timeline__meta">{ev.user_name} · {formatDateTime(ev.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="contract-side">
          <div className="card">
            <h3 className="card-title">Подписание</h3>
            {contract.signature_verified ? (
              <div className="sign-status sign-status--ok">
                <CheckCircle2 size={18} />
                <div>
                  <div>Подпись подтверждена</div>
                  {contract.signature_info?.signers?.[0] && <div className="muted-text">{contract.signature_info.signers[0].name}</div>}
                </div>
              </div>
            ) : (
              <div className="sign-status"><Clock size={18} /><span>Не подписан</span></div>
            )}
            {canEdit && (
              <>
                <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate(f); }} />
                <Button style={{ width: '100%', marginTop: 12 }} disabled={uploadMutation.isPending} onClick={() => fileRef.current?.click()}>
                  <Upload size={16} />{uploadMutation.isPending ? 'Загрузка…' : 'Загрузить подписанный PDF'}
                </Button>
                {contract.signature_verified && contract.status === 'signing' && (
                  <Button variant="primary" style={{ width: '100%', marginTop: 8 }} disabled={confirmMutation.isPending} onClick={() => confirmMutation.mutate()}>
                    Подтвердить и в работу
                  </Button>
                )}
              </>
            )}
          </div>
          {canEdit && NEXT_STATUS[contract.status].length > 0 && (
            <div className="card">
              <h3 className="card-title">Действия</h3>
              <div className="status-actions">
                {NEXT_STATUS[contract.status].map((s) => (
                  <Button key={s.value} style={{ width: '100%' }} disabled={statusMutation.isPending} onClick={() => statusMutation.mutate(s.value)}>
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {contract.type === 'customer' && (
            <div className="card">
              <h3 className="card-title">Субподряды</h3>
              <div className="big-stat">{contract.subcontracts_count}</div>
              <div className="muted-text">договоров субподряда</div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="detail-row">
      <dt>{label}</dt>
      <dd className={mono ? 'mono' : ''}>{value}</dd>
    </div>
  );
}
